import dayjs from "dayjs";
import { get } from "lodash";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  MdOutlineBadge,
  MdOutlineCake,
  MdOutlineChangeCircle,
  MdOutlineHistory,
  MdSocialDistance,
} from "react-icons/md";
import Alert from "shared/components/Alert";
import Input from "shared/components/Input";
import { NAME_SPACES } from "shared/locales/constants";
import format from "string-template";
import { DATE_FORMAT } from "utils/constants";
import { isAcorisRiaBlocked } from "screens/Private/Broker/Projects/Detail/Templates/Comparison/Acoris/utils/riaBlock";
import { createEmptyAcorisTerminationRequest } from "screens/Private/Broker/Projects/Detail/Templates/Comparison/Acoris/utils/terminationRequest";
import {
  bindDateInputProps,
  bindInputProps,
  bindTextFieldInputProps,
} from "utils/helpers/input";
import { filterContractNumber } from "utils/helpers/validations";

export const step = { CONTRACT: "CONTRACT" };

const clearRiaDetails = (setFieldValue) => {
  setFieldValue("contractOlderThanOneYear", false);
  setFieldValue("terminationRequest", null);
  setFieldValue("ria", false);
};

const Contract = ({
  index,
  translation,
  deltaRia,
  deltaStart,
  retroactivityEnabled,
  values,
  setFieldValue,
  ...formProps
}) => {
  const { t: tErrors } = useTranslation(NAME_SPACES.ERRORS);
  const form = translation.TEMPLATES.CONTACT.STEPS[step.CONTRACT].FORM;
  const isRia = values.ria === true;
  const isRetroactive = values.retroactive === true;
  const hasOtherInsurer = get(values, "hasOtherInsurer") === true;
  const contractOlderThanOneYear =
    get(values, "contractOlderThanOneYear") === true;
  const riaBlocked = isAcorisRiaBlocked({
    hasOtherInsurer,
    contractOlderThanOneYear: get(values, "contractOlderThanOneYear"),
  });
  const [pickerValue, setPickerValue] = useState(
    !get(values, "contract.issuanceDate") && dayjs(),
  );
  const [showInfo, setShowInfo] = useState(false);

  const minIssuanceDate = isRetroactive
    ? dayjs().startOf("month")
    : dayjs()
        .startOf("day")
        .add(isRia ? deltaRia : deltaStart, "days");
  const isExpired =
    values.expired ||
    (values?.contract?.issuanceDate &&
      dayjs(values.contract.issuanceDate).isBefore(minIssuanceDate));

  const syncRiaIssuance = (nextIsRia) => {
    setFieldValue("ria", nextIsRia);
    if (nextIsRia) {
      setFieldValue("retroactive", false);
      const nextMin = dayjs().startOf("day").add(deltaRia, "days");
      const current = values?.contract?.issuanceDate;
      if (!current || dayjs(current).isBefore(nextMin)) {
        setShowInfo(true);
        setFieldValue("contract.issuanceDate", nextMin.toDate());
      }
      setPickerValue(dayjs().add(deltaRia, "days"));
      return;
    }
    setShowInfo(false);
    setPickerValue(dayjs());
  };

  return (
    <div className="contact-stepper" key={step.CONTRACT}>
      <div className="contact-stepper__header">
        <div className="contact-stepper__header--counter">
          <span>{index}</span>
        </div>
        <div className="contact-stepper__header--content">
          <h1 className="contact-stepper__header--content--title">
            <span>
              {translation.TEMPLATES.CONTACT.STEPS[step.CONTRACT].TITLE}
            </span>
          </h1>
          <p className="contact-stepper__header--content--description">
            {translation.TEMPLATES.CONTACT.STEPS[step.CONTRACT].DESCRIPTION}
          </p>
        </div>
      </div>
      <div className="contact-stepper__content">
        {isExpired && (
          <Alert
            showIcon
            type="error"
            message={
              translation.TEMPLATES.CONTACT.STEPS[step.CONTRACT].INVALID_DATE
            }
            description={format(
              translation.TEMPLATES.CONTACT.STEPS[step.CONTRACT]
                .INVALID_ISSUANCE,
              {
                date: minIssuanceDate.format(DATE_FORMAT),
              },
            )}
          />
        )}
        <Input
          type="switch"
          icon={<MdOutlineChangeCircle size={22} />}
          label={form.CONTRACT.LABEL}
          checkedChildren={form.CONTRACT.OPTIONS.YES}
          unCheckedChildren={form.CONTRACT.OPTIONS.NO}
          checked={hasOtherInsurer}
          onChange={(value) => {
            setFieldValue("hasOtherInsurer", value);
            if (!value) {
              clearRiaDetails(setFieldValue);
              setShowInfo(false);
              setPickerValue(dayjs());
              return;
            }
            setFieldValue("contractOlderThanOneYear", null);
            setFieldValue("ria", false);
          }}
        />
        {hasOtherInsurer && (
          <>
            <Input
              type="radio"
              variant="group"
              required
              label={form.OLDER_THAN_ONE_YEAR.LABEL}
              values={[
                {
                  label: form.OLDER_THAN_ONE_YEAR.OPTIONS.YES,
                  value: true,
                },
                {
                  label: form.OLDER_THAN_ONE_YEAR.OPTIONS.NO,
                  value: false,
                },
              ]}
              value={get(values, "contractOlderThanOneYear")}
              errors={get(formProps, "errors.contractOlderThanOneYear")}
              touched={get(formProps, "touched.contractOlderThanOneYear")}
              onChange={(value) => {
                const olderThanOneYear = value === true || value === "true";
                setFieldValue("contractOlderThanOneYear", olderThanOneYear);
                if (!olderThanOneYear) {
                  setFieldValue("terminationRequest", null);
                  syncRiaIssuance(false);
                  return;
                }
                setFieldValue(
                  "terminationRequest",
                  createEmptyAcorisTerminationRequest(),
                );
                syncRiaIssuance(true);
              }}
            />
            {riaBlocked && (
              <Alert
                showIcon
                type="error"
                message={tErrors("VALIDATIONS.ACORIS.RIA_UNDER_ONE_YEAR")}
              />
            )}
            {contractOlderThanOneYear && (
              <>
                <Input
                  required
                  icon={<MdOutlineCake size={22} />}
                  label={form.CURRENT_CONTRACT_EFFECT_DATE.LABEL}
                  placeholder={form.CURRENT_CONTRACT_EFFECT_DATE.PLACEHOLDER}
                  type="date"
                  variant="simple"
                  format={DATE_FORMAT}
                  defaultPickerValue={dayjs().subtract(1, "year")}
                  disabledDate={(current) =>
                    current && current > dayjs().subtract(1, "year")
                  }
                  {...bindDateInputProps({
                    name: "terminationRequest.dueDate",
                    values,
                    setFieldValue,
                    ...formProps,
                  })}
                />
                <Input
                  required
                  icon={<MdOutlineBadge size={22} />}
                  label={form.CURRENT_CONTRACT.LABEL}
                  placeholder={form.CURRENT_CONTRACT.PLACEHOLDER}
                  type="custom"
                  stripSpaces
                  {...bindInputProps({
                    name: "terminationRequest.contractNumber",
                    values,
                    setFieldValue,
                    ...formProps,
                  })}
                  onChange={({ target: { value } }) => {
                    setFieldValue(
                      "terminationRequest.contractNumber",
                      filterContractNumber(value),
                    );
                  }}
                />
                <Input
                  required
                  icon={<MdOutlineBadge size={22} />}
                  label={form.CURRENT_COMPANY.LABEL}
                  placeholder={form.CURRENT_COMPANY.PLACEHOLDER}
                  {...bindTextFieldInputProps({
                    name: "terminationRequest.brokerageMandate.currentCompany",
                    values,
                    setFieldValue,
                    ...formProps,
                  })}
                />
              </>
            )}
          </>
        )}
        <Input
          type="switch"
          icon={<MdOutlineHistory size={22} />}
          label={form.RETROACTIVE.LABEL}
          checkedChildren={form.RETROACTIVE.OPTIONS.YES}
          unCheckedChildren={form.RETROACTIVE.OPTIONS.NO}
          checked={isRetroactive}
          onChange={(value) => {
            setFieldValue("retroactive", value);
          }}
          disabled={isRia || !retroactivityEnabled}
        />
        <Input
          required
          icon={<MdOutlineCake size={22} />}
          label={form.START_DATE.LABEL}
          placeholder={form.START_DATE.PLACEHOLDER}
          type="date"
          variant="simple"
          format={DATE_FORMAT}
          disabledDate={(current) => current && current < minIssuanceDate}
          defaultPickerValue={pickerValue}
          {...bindDateInputProps({
            name: "contract.issuanceDate",
            values,
            setFieldValue,
            ...formProps,
          })}
        />
        {showInfo && (
          <Alert
            showIcon
            type="info"
            message={format(
              translation.TEMPLATES.CONTACT.STEPS[step.CONTRACT]
                .INVALID_ISSUANCE,
              {
                date: dayjs().add(deltaRia, "days").format(DATE_FORMAT),
              },
            )}
          />
        )}
        <Input
          type="switch"
          icon={<MdSocialDistance size={22} />}
          label={form.CONTRACT_TYPE.LABEL}
          checkedChildren={form.CONTRACT_TYPE.OPTIONS.YES}
          unCheckedChildren={form.CONTRACT_TYPE.OPTIONS.NO}
          checked={get(values, "fields.twoTimesSale") === true}
          onChange={(value) => {
            setFieldValue("fields.twoTimesSale", value);
          }}
        />
      </div>
    </div>
  );
};
export default Contract;
